import { memo, useCallback, useState } from 'react';
import styled from 'styled-components';

import { AddIcon, Button, LogoIcon, TextField, Wrapper } from 'components';
import Select from 'components/Select';
import useTheme from 'hooks/useTheme';
import ApiServiceV2 from 'services/apiService/apiServiceV2';
import { debounce, filterOption } from 'utils/libs';

const { Input } = TextField;
const { Option } = Select;

const initialData = { isFetching: false, options: [] };
const initialNewSite = { idc: '', name: '' };

const CreateSiteAction = styled.button`
	display: flex;
	width: 100%;
	min-height: 44px;
	align-items: center;
	gap: 10px;
	padding: 10px 16px;
	background: ${({ $newTheme }) => $newTheme._secondaryBackgroundColor};
	border: 0;
	border-top: 1px solid ${({ $newTheme }) => $newTheme._borderColor};
	color: ${({ $newTheme }) => $newTheme._secondaryColor};
	font: inherit;
	font-size: 14px;
	font-weight: 600;
	line-height: 20px;
	text-align: left;
	cursor: pointer;
	transition:
		background-color 0.15s ease,
		color 0.15s ease;

	&:hover:not(:disabled) {
		background: ${({ $newTheme }) => $newTheme._surfaceBackgroundColor};
		color: ${({ $newTheme }) => $newTheme._highEmphasisColor};
	}

	&:focus-visible {
		outline: 2px solid ${({ $newTheme }) => $newTheme._secondaryColor};
		outline-offset: -2px;
		background: ${({ $newTheme }) => $newTheme._surfaceBackgroundColor};
	}

	&:disabled {
		color: ${({ $newTheme }) => $newTheme._inactiveColor};
		cursor: not-allowed;
	}
`;

/**
 * The clientIdc component is a site selector.  It stays data-driven for the
 * normal lookup and only exposes the local site creation panel when the form
 * explicitly opts in through config.allowCreateSite.
 */
const ClientIdcField = memo(
	({
		componentId,
		componentParam,
		config,
		disabled,
		getProcessedComponentSqlData,
		onChange,
		paramValues,
		placeholder,
		setValues,
		title,
		value,
	}) => {
		const { theme } = useTheme();
		const [data, setData] = useState(initialData);
		const [isCreating, setIsCreating] = useState(false);
		const [isCreatingSite, setIsCreatingSite] = useState(false);
		const [isSelectorOpen, setIsSelectorOpen] = useState(false);
		const [creationError, setCreationError] = useState(null);
		const [newSite, setNewSite] = useState(initialNewSite);

		const fetchData = useCallback(
			async searchValue => {
				setData(prev => ({ ...prev, isFetching: true }));
				const options = await getProcessedComponentSqlData({
					componentId,
					searchValue,
					paramValues,
					returnKey: 'options',
				});

				setData({
					isFetching: false,
					options: Array.isArray(options) ? options : [],
				});
			},
			[componentId, getProcessedComponentSqlData, paramValues],
		);

		const fetchDebouncedData = useCallback(debounce(fetchData, 700), [
			fetchData,
		]);
		const canCreateSite =
			config?.allowCreateSite &&
			newSite.idc.trim().length > 0 &&
			newSite.name.trim().length > 0;

		const selectExistingSite = selectedValue => {
			setValues(prev => ({ ...prev, [componentParam]: selectedValue }));
			onChange(selectedValue);
			setIsCreating(false);
			setIsSelectorOpen(false);
		};

		const openSiteCreation = () => {
			if (!disabled) {
				setIsCreating(true);
				setIsSelectorOpen(false);
			}
		};

		const createSite = async () => {
			if (!canCreateSite) return;

			const clientIdc = newSite.idc.trim();
			setIsCreatingSite(true);
			setCreationError(null);

			try {
				await ApiServiceV2.post({
					url: '/clients/v1',
					data: { idc: clientIdc, name: newSite.name.trim() },
				});
				selectExistingSite(clientIdc);
				setNewSite(initialNewSite);
				setIsCreating(false);
			} catch (error) {
				setCreationError(error?.message || 'No se pudo crear el sitio');
			} finally {
				setIsCreatingSite(false);
			}
		};

		return (
			<Wrapper
				width='100%'
				padding='0'
				flexDirection='column'
				alignItems='stretch'
			>
				<Select
					width='100%'
					disabled={disabled}
					showSearch
					filterOption={filterOption}
					value={value}
					open={isSelectorOpen}
					placeholder={placeholder || title}
					onSearch={fetchDebouncedData}
					onChange={selectExistingSite}
					onDropdownVisibleChange={isOpen => {
						setIsSelectorOpen(isOpen);
						if (isOpen) fetchData();
					}}
					dropdownRender={menu => (
						<>
							{data.isFetching && !data.options.length ? (
								<Wrapper width='100%' justifyContent='center'>
									<LogoIcon spin={true} />
								</Wrapper>
							) : (
								menu
							)}
							{config?.allowCreateSite && (
								<CreateSiteAction
									type='button'
									disabled={disabled}
									$newTheme={theme}
									onMouseDown={event => event.preventDefault()}
									onClick={openSiteCreation}
								>
									<AddIcon
										bold
										size='small'
										fill={
											disabled ? theme._inactiveColor : theme._secondaryColor
										}
									/>
									<span>Crear sitio</span>
								</CreateSiteAction>
							)}
						</>
					)}
				>
					{data.options.map(item => (
						<Option key={item.id} value={item.id}>
							{item.name}
						</Option>
					))}
				</Select>

				{config?.allowCreateSite && isCreating && (
					<Wrapper
						padding='10px 0 0 0'
						flexDirection='column'
						alignItems='stretch'
					>
						<Input
							width='100%'
							placeholder='IDC del sitio'
							value={newSite.idc}
							onChange={event =>
								setNewSite(prev => ({ ...prev, idc: event.target.value }))
							}
						/>
						<Input
							width='100%'
							margin='8px 0 0 0'
							placeholder='Nombre del sitio'
							value={newSite.name}
							onChange={event =>
								setNewSite(prev => ({ ...prev, name: event.target.value }))
							}
						/>
						<Button
							margin='8px 0 0 0'
							mode='primary'
							disabled={!canCreateSite || isCreatingSite}
							onClick={createSite}
						>
							{isCreatingSite
								? 'Creando sitio...'
								: 'Crear y seleccionar sitio'}
						</Button>
						{creationError && (
							<small style={{ color: '#d4380d', marginTop: 8 }}>
								{creationError}
							</small>
						)}
					</Wrapper>
				)}
			</Wrapper>
		);
	},
);

export default ClientIdcField;
